/**
 * Hover tooltip system for map markers.
 * Single reusable DOM element positioned at cursor, filled with sanitized event data.
 */

import type { NormalizedEvent, Severity } from '@/sources/base-source';
import { relativeTime } from '@/utils/time';

const SEVERITY_BORDER_COLOR: Record<Severity, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  info: '#64748b',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'CRITICAL',
  high: 'HIGH',
  medium: 'MEDIUM',
  low: 'LOW',
  info: 'INFO',
};

let tooltipEl: HTMLDivElement | null = null;

/** Lazily create and return the tooltip DOM element. */
function ensureTooltipElement(container: HTMLElement): HTMLDivElement {
  if (tooltipEl) return tooltipEl;

  tooltipEl = document.createElement('div');
  tooltipEl.className = 'sentinel-tooltip';
  tooltipEl.style.cssText = [
    'position: absolute',
    'pointer-events: none',
    'z-index: 10',
    'display: none',
    'max-width: 280px',
    'padding: 8px 10px',
    'border-radius: 4px',
    'background: var(--bg-overlay, rgba(17, 17, 17, 0.92))',
    'border-left: 3px solid var(--text-muted)',
    'color: var(--text-primary)',
    'font-family: var(--font-mono)',
    'font-size: 11px',
    'line-height: 1.4',
    'white-space: normal',
    'word-wrap: break-word',
    'backdrop-filter: blur(4px)',
    'box-shadow: var(--shadow-md)',
    'transition: opacity 0.1s ease',
  ].join('; ');

  container.appendChild(tooltipEl);
  return tooltipEl;
}

/**
 * Build tooltip content from a NormalizedEvent using safe DOM APIs.
 * No innerHTML — all dynamic values assigned via textContent.
 */
function buildTooltipDOM(event: NormalizedEvent): DocumentFragment {
  const frag = document.createDocumentFragment();
  const severity = event.severity ?? 'info';
  const borderColor = SEVERITY_BORDER_COLOR[severity];
  const label = SEVERITY_LABEL[severity];

  // Title
  const titleEl = document.createElement('div');
  titleEl.style.cssText = 'font-weight: 600; margin-bottom: 4px; color: var(--text-primary);';
  titleEl.textContent = event.title;
  frag.appendChild(titleEl);

  // Severity badge + source
  const metaRow = document.createElement('div');
  metaRow.style.cssText = 'margin-bottom: 4px;';

  const badge = document.createElement('span');
  badge.style.cssText = `display: inline-block; padding: 1px 5px; border-radius: 2px; font-size: 9px; font-weight: 700; letter-spacing: 0.5px; background: ${borderColor}22; color: ${borderColor}; border: 1px solid ${borderColor}44;`;
  badge.textContent = label;
  metaRow.appendChild(badge);

  const sourceSpan = document.createElement('span');
  sourceSpan.style.cssText = 'color: var(--text-secondary); margin-left: 6px;';
  sourceSpan.textContent = event.source;
  metaRow.appendChild(sourceSpan);

  frag.appendChild(metaRow);

  // Location name (if available)
  if (event.location?.name) {
    const locEl = document.createElement('div');
    locEl.style.cssText = 'color: var(--text-secondary); margin-bottom: 2px;';
    locEl.textContent = event.location.name;
    frag.appendChild(locEl);
  }

  // Relative timestamp
  const timeEl = document.createElement('div');
  timeEl.style.cssText = 'color: var(--text-muted); font-size: 10px;';
  timeEl.textContent = relativeTime(event.timestamp);
  frag.appendChild(timeEl);

  return frag;
}

/** The container element the tooltip is attached to. */
let tooltipContainer: HTMLElement | null = null;

/** Initialize the tooltip system. Must be called after the map container exists. */
export function initTooltip(container: HTMLElement): void {
  tooltipContainer = container;
  ensureTooltipElement(container);
}

/** Show the tooltip at a given screen position with event data. */
export function showTooltip(x: number, y: number, event: NormalizedEvent): void {
  if (!tooltipContainer) return;
  const el = ensureTooltipElement(tooltipContainer);

  const severity = event.severity ?? 'info';
  el.style.borderLeftColor = SEVERITY_BORDER_COLOR[severity];
  el.textContent = '';
  el.appendChild(buildTooltipDOM(event));
  el.style.display = 'block';

  // Position with offset to avoid cursor overlap
  const offsetX = 12;
  const offsetY = 12;
  const containerRect = tooltipContainer.getBoundingClientRect();

  let left = x + offsetX;
  let top = y + offsetY;

  // Clamp to container bounds (flip if near edges)
  const elWidth = el.offsetWidth || 280;
  const elHeight = el.offsetHeight || 80;

  if (left + elWidth > containerRect.width) {
    left = x - elWidth - offsetX;
  }
  if (top + elHeight > containerRect.height) {
    top = y - elHeight - offsetY;
  }

  // Ensure non-negative
  left = Math.max(0, left);
  top = Math.max(0, top);

  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

/** Hide the tooltip. */
export function hideTooltip(): void {
  if (tooltipEl) {
    tooltipEl.style.display = 'none';
  }
}

/** Remove the tooltip DOM element and clean up. */
export function destroyTooltip(): void {
  if (tooltipEl) {
    tooltipEl.remove();
    tooltipEl = null;
  }
  tooltipContainer = null;
}
